import { tryOnMounted } from '@vueuse/core';
import $ from 'jquery';
import { nextTick, type Ref, ref, unref, watch } from 'vue';

import { errutil, purl, tabledata } from '#public/commons-global';
import type { NoShowError } from '#public_ts/commons/fe-commons';
import { useGlobalConfirm, type ValidateFormApi } from '#public_ts/components';

import { createFormInitializer, createSumParser } from './form-state';
import { ERR, ORDER } from './globals';
import type { ProductFormData, ProductFormState, SubmitApi } from './types';
import { __STOPLIST__ } from './utils';
import { useValidator } from './validator';

export function useProductFormHandler(
    formData: ProductFormData,
    formState: ProductFormState,
    formApi: Ref<ValidateFormApi | undefined>,
    submitApi: Ref<SubmitApi | undefined>,
    options: {
        disableSumParser?: boolean;
    } = {},
) {
    const confirm = useGlobalConfirm();
    const sumParser = createSumParser(formData, formState, confirm, () => {
        unref(formApi)?.validateForm();
    });
    const formInitializer = createFormInitializer(formData, formState);
    const { validator } = useValidator(formData, formState);

    const updatePromotionDebounced = tabledata.debouncedFunction(() => sumParser.updatePromotion(), 500, true);
    const updateQuantityDebounced = tabledata.debouncedFunction(() => sumParser.updateQuantity(), 500, true);
    const initialized = ref(false);
    const chatWidgetOpen = ref(false);

    function initialize() {
        if(ERR) {
            errutil.box(ERR);
            return;
        }

        const url = purl();
        const urlParams = url.param();
        const base = url.attr('base') + __ROOT__;
        formData.Base_url = base;

        if(ORDER && (urlParams['ignore_lastorder'] != 'true')) {
            formInitializer.fillOrderFields(ORDER);
        }

        const data = urlParams['data'];
        if(data) {
            try {
                const _data = JSON.parse(window.atob(data));
                formInitializer.fillOrderFieldsFormFattappQr(_data);
            } catch(_err) { /**/ }
        }

        if(urlParams['cumulative_agent_discount'] == 'true') {
            const Company_Tax_Number = formData.Company_Tax_Number;
            const Company_Fiscal_Code = formData.Company_Fiscal_Code;
            const discountCode = Company_Tax_Number || Company_Fiscal_Code;
            if(discountCode) {
                formState.Product_Promotion_Code_flag = true;
                formData.Product_Promotion_Code = discountCode;
            }
        }

        const callback = urlParams['callback'];
        if(callback) {
            if(callback == 'registerCallback') {
                formState.paymentNoneForceHistoryBack = true;
            } else if(callback.startsWith('redirect:')) {
                formState.redirectTo = callback.substring(9);
            }
        }

        chatWidgetOpen.value = urlParams['chat_widget_open'] == 'true';

        formData.codice_destinatario = 'WHP7LTE';
        formData.payment_method = 'bank';
        initialized.value = true;
    }

    tryOnMounted(() => {
        nextTick(() => {
            $(() => {
                initialize();
                if(!options.disableSumParser) {
                    sumParser.init();
                    sumParser.updateQuantity();
                    updatePromotionDebounced(true);
                }
            });
        });
    });

    watch(() => formData.Product_Quantity, () => {
        if(initialized.value) updateQuantityDebounced();
    });

    watch(() => formState.Product_Promotion_Code_flag, () => {
        if(initialized.value) updatePromotionDebounced();
    });

    function checkStopList() {
        function fixword(word: string) {
            return word.toLowerCase().replace(/\s+/, '');
        }
        const WARNINGS = {
            // eslint-disable-next-line @stylistic/max-len
            Company_Name: "La 'Ragione Sociale' digitata non sembra coretta (occorre digitare la 'Ragione Sociale' in forma completa). Se ritieni sia giusta, premi OK.",
        };

        const Company_Name = fixword(formData.Company_Name ?? '');
        const haveStopword = __STOPLIST__.Company_Name.some((word) => {
            const word0 = fixword(word);
            if(word0 == Company_Name) {
                return true;
            }
            return false;
        });
        if(haveStopword) {
            return confirm(WARNINGS.Company_Name, { cancel: true, rejectOnCancel: true })
                .then(() => undefined);
        }
        return Promise.resolve(undefined);
    }

    function submit() {
        Promise.resolve()
            .then(() => checkStopList())
            .then(() => {
                if(!options.disableSumParser) {
                    return sumParser.updatePromotion({ nowait: false, errors: true });
                }
                return Promise.resolve();
            })
            .then(() => {
                if(!options.disableSumParser) {
                    if(formState.isPromotionCodeValid !== true) {
                        return;
                    }
                }
                submitApi.value?.performSubmit(formData, formState);
            })
            .catch((err: NoShowError) => {
                if(err && !err.no_show_error) errutil.box(null, err);
            });
    }

    return {
        updateQuantity: () => sumParser.updateQuantity(),
        updateUserDiscountCode: () => sumParser.updateUserDiscountCode(),
        updateQuantityDebounced: () => updateQuantityDebounced(),
        updatePromotion: () => updatePromotionDebounced(),
        validator,
        submit,
        initialized,
        chatWidgetOpen,
    };
}
